import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { Upload, Trash2, ImageIcon, Loader2, X, Camera } from "lucide-react";

/**
 * Generic photo/file gallery backed by /api/files endpoints.
 * Lists, uploads (multiple), previews and soft-deletes files.
 *
 * Props:
 *   ownerType: "car" | "rental_pickup" | "rental_return" | "customer"
 *   ownerId:   string
 *   accept:    file accept attribute (default images)
 *   gridCols:  number (default 4)
 *   compact:   bool (small thumbnails)
 *   title:     optional section title
 *   description: optional helper text
 */
export default function PhotoGallery({
  ownerType,
  ownerId,
  accept = "image/*",
  gridCols = 4,
  compact = false,
  title,
  description,
  onUploaded,
}) {
  const { t } = useT();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [thumbs, setThumbs] = useState({}); // id -> blob url
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [preview, setPreview] = useState(null); // { url, name }

  const load = async () => {
    if (!ownerId) return;
    try {
      const { data } = await api.get("/files", {
        params: { owner_type: ownerType, owner_id: ownerId },
      });
      setFiles(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    return () => {
      // cleanup blob urls
      Object.values(thumbs).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerType, ownerId]);

  // Lazy load thumbnails (as blobs to support cookie auth)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const f of files) {
        if (thumbs[f.id]) continue;
        if (!f.content_type?.startsWith("image/")) continue;
        try {
          const res = await api.get(`/files/${f.id}/raw`, { responseType: "blob" });
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          setThumbs((prev) => ({ ...prev, [f.id]: url }));
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const handleFiles = async (selected) => {
    if (!selected?.length) return;
    setUploading(true);
    setProgress({ done: 0, total: selected.length });
    let okCount = 0;
    for (let i = 0; i < selected.length; i++) {
      const file = selected[i];
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      try {
        await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        okCount += 1;
      } catch (e) {
        toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail)}`);
      }
      setProgress({ done: i + 1, total: selected.length });
    }
    setUploading(false);
    if (okCount > 0) toast.success(t("photos.uploadedN", { n: okCount }));
    await load();
    if (inputRef.current) inputRef.current.value = "";
    if (okCount > 0) onUploaded?.({ count: okCount });
  };

  const onSelect = (e) => {
    const list = Array.from(e.target.files || []);
    handleFiles(list);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const list = Array.from(e.dataTransfer.files || []);
    handleFiles(list);
  };

  const remove = async (f) => {
    if (!window.confirm(t("photos.confirmDelete"))) return;
    try {
      await api.delete(`/files/${f.id}`);
      // revoke blob
      if (thumbs[f.id]) URL.revokeObjectURL(thumbs[f.id]);
      setThumbs((prev) => {
        const c = { ...prev };
        delete c[f.id];
        return c;
      });
      toast.success(t("photos.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openPreview = (f) => {
    if (thumbs[f.id]) setPreview({ url: thumbs[f.id], name: f.original_filename });
  };

  const colsCls = {
    2: "grid-cols-2",
    3: "grid-cols-2 sm:grid-cols-3",
    4: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4",
    5: "grid-cols-2 sm:grid-cols-3 md:grid-cols-5",
    6: "grid-cols-3 sm:grid-cols-4 md:grid-cols-6",
  }[gridCols] || "grid-cols-4";

  const aspectCls = compact ? "aspect-square" : "aspect-[4/3]";

  return (
    <div className="space-y-3" data-testid={`gallery-${ownerType}-${ownerId}`}>
      {(title || description) && (
        <div>
          {title && <div className="overline">{title}</div>}
          {description && <p className="text-xs text-zinc-500 mt-1">{description}</p>}
        </div>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="border-2 border-dashed border-zinc-300 hover:border-[#F5A623] transition-colors p-4 bg-zinc-50/50"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-zinc-600">
            <Camera className="w-4 h-4 text-[#F5A623]" />
            <span>{t("photos.dropHint")}</span>
          </div>
          <div className="flex items-center gap-2">
            {uploading && (
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                {progress.done}/{progress.total}
              </div>
            )}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading || !ownerId}
              className="inline-flex items-center gap-2 bg-black hover:bg-[#F5A623] text-white text-xs px-3 py-1.5 transition-colors disabled:opacity-50"
              data-testid={`gallery-upload-${ownerType}`}
            >
              <Upload className="w-3 h-3" /> {t("photos.uploadBtn")}
            </button>
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          onChange={onSelect}
          className="hidden"
          data-testid={`gallery-file-input-${ownerType}`}
        />
      </div>

      {files.length === 0 ? (
        <div className="border border-zinc-200 p-6 text-center bg-white">
          <ImageIcon className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
          <div className="text-xs text-zinc-500">{t("photos.empty")}</div>
        </div>
      ) : (
        <div className={`grid ${colsCls} gap-2`}>
          {files.map((f) => (
            <div key={f.id} className="relative group border border-zinc-200 bg-white">
              <button
                type="button"
                onClick={() => openPreview(f)}
                className={`w-full ${aspectCls} bg-zinc-100 overflow-hidden block`}
                data-testid={`photo-thumb-${f.id}`}
              >
                {thumbs[f.id] ? (
                  <img src={thumbs[f.id]} alt={f.original_filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={() => remove(f)}
                className="absolute top-1 right-1 bg-white/95 hover:bg-red-600 hover:text-white border border-zinc-200 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`photo-delete-${f.id}`}
                aria-label="delete"
              >
                <Trash2 className="w-3 h-3" />
              </button>
              {f.label && (
                <div className="px-2 py-1 text-[10px] font-mono uppercase truncate border-t border-zinc-100">{f.label}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="photo-preview-modal"
        >
          <button
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 text-white hover:text-[#F5A623] p-2"
            aria-label="close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={preview.url}
            alt={preview.name}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
